import { createContext, useContext, useMemo, useState, useCallback } from 'react';

type AuthState = {
  token: string | null;
  username: string | null;
  setSession: (token: string, username: string) => void;
  logout: () => void;
};

const KEY_TOKEN = 'assets_admin_token';
const KEY_USER = 'assets_admin_user';

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(KEY_TOKEN));
  const [username, setUsername] = useState<string | null>(() => localStorage.getItem(KEY_USER));

  const setSession = useCallback((t: string, u: string) => {
    localStorage.setItem(KEY_TOKEN, t);
    localStorage.setItem(KEY_USER, u);
    setToken(t);
    setUsername(u);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(KEY_TOKEN);
    localStorage.removeItem(KEY_USER);
    setToken(null);
    setUsername(null);
  }, []);

  const value = useMemo(
    () => ({ token, username, setSession, logout }),
    [token, username, setSession, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
